import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'cat-shoes-01': 'ri-footprint-line',
  'cat-hoodies-02': 'ri-t-shirt-line',
  'cat-tshirts-03': 'ri-shirt-line',
  'cat-jackets-04': 'ri-cloudy-line',
  'cat-pants-05': 'ri-pantone-line',
  'cat-headwear-06': 'ri-sun-line',
  'cat-sets-07': 'ri-group-line',
  'cat-underwear-08': 'ri-shirt-line',
  'cat-jersey-09': 'ri-run-line',
  'cat-accessories-10': 'ri-vip-diamond-line',
  'cat-bags-11': 'ri-briefcase-line',
  'cat-belts-12': 'ri-circle-line',
  'cat-perfumes-13': 'ri-drop-line',
  'cat-other-14': 'ri-more-line',
};

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textColor = isScrolled ? 'text-foreground-950' : 'text-background-50';
  const textMuted = isScrolled ? 'text-foreground-400' : 'text-background-50/60';
  const borderColor = isScrolled ? 'border-foreground-200' : 'border-background-50/30';
  const buttonBg = isScrolled ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800' : 'bg-background-50 text-foreground-950 hover:bg-background-100';
  const dropdownBg = isScrolled ? 'bg-background-50 border-background-200' : 'bg-background-50 border-background-200';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 shrink-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isScrolled ? 'bg-foreground-950' : 'bg-background-50/20'}`}>
              <i className={`ri-database-2-line text-lg ${isScrolled ? 'text-background-50' : 'text-background-50'}`}></i>
            </div>
            <span className={`font-bold text-sm md:text-base whitespace-nowrap ${textColor}`}>
              cssbuyspreadsheet<span className={textMuted}>.blog</span>
            </span>
          </a>

          {/* Center: Navigation Links */}
          <div className="hidden md:flex items-center gap-2 ml-6">
            <a
              href="/"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              <i className="ri-home-line mr-1.5"></i>
              Início
            </a>
            <a
              href="/guia-de-compras"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              <i className="ri-book-open-line mr-1.5"></i>
              Guia de Compras
            </a>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200 cursor-pointer ${
                  isScrolled
                    ? 'border-foreground-200 bg-background-50/80 text-foreground-700 hover:border-foreground-400'
                    : 'border-background-50/30 bg-transparent text-background-50/90 hover:border-background-50/60 hover:bg-background-50/10'
                }`}
              >
                <i className="ri-grid-line text-base"></i>
                <span>Categorias</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className={`absolute top-full mt-2 left-0 w-72 ${dropdownBg} rounded-2xl shadow-lg py-3 max-h-[420px] overflow-y-auto`}>
                  <div className="px-4 pb-2 mb-2 border-b border-background-200">
                    <p className="text-foreground-400 text-xs font-medium">ESCOLHA UMA CATEGORIA</p>
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-foreground-700 hover:bg-background-100 transition-colors duration-150 text-sm"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                        <i className={`${categoryIcons[cat.seq] || 'ri-arrow-right-up-line'} text-foreground-400 text-sm`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: CTA Buttons */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${buttonBg}`}
            >
              buy it now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full border cursor-pointer transition-colors duration-200 ${
                isScrolled
                  ? 'border-foreground-200 text-foreground-700'
                  : 'border-background-50/40 text-background-50'
              }`}
            >
              <i className={isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 max-h-[70vh] overflow-y-auto">
          {/* Mobile links */}
          <div className="flex flex-col gap-1 mb-4">
            <a
              href="/"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground-700 hover:bg-background-100 rounded-xl transition-colors duration-150 text-sm font-medium"
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                <i className="ri-home-line text-accent-600 text-sm"></i>
              </div>
              Início
            </a>
            <a
              href="/guia-de-compras"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground-700 hover:bg-background-100 rounded-xl transition-colors duration-150 text-sm font-medium"
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                <i className="ri-book-open-line text-accent-600 text-sm"></i>
              </div>
              Guia de Compras
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-foreground-700 hover:bg-background-100 rounded-xl transition-colors duration-150 text-sm font-medium"
            >
              <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                <i className="ri-shopping-cart-line text-background-50 text-sm"></i>
              </div>
              buy it now
            </a>
          </div>

          {/* Category divider */}
          <div className="border-t border-background-200 pt-4">
            <p className="text-foreground-400 text-xs font-semibold px-4 mb-2">CATEGORIAS</p>
            <div className="flex flex-col gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3 text-foreground-700 hover:bg-background-100 rounded-xl transition-colors duration-150 text-sm"
                  onClick={() => setIsMobileOpen(false)}
                >
                  <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                    <i className={`${categoryIcons[cat.seq] || 'ri-arrow-right-up-line'} text-foreground-400 text-sm`}></i>
                  </div>
                  <span>{cat.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}